/**
 * Client view of the billing catalog + entitlement rules.
 *
 * The source of truth is the server (`functions/billing/catalog.js`, edited in
 * Admin → Finances) mirrored to `site_config/plans_public`. DEFAULT_PUBLIC_CATALOG
 * is only the first-paint fallback before that doc loads. `effectivePlan` mirrors
 * `functions/billing/subscriptionState.js` (tested side by side).
 */

export const PLAN_ORDER = ['FREE', 'BASIC', 'PRO', 'ULTIMATE'];
const RANK = { FREE: 0, BASIC: 1, PRO: 2, ULTIMATE: 3 };

const p = (amount, sale = true) => ({ amount, live: sale, test: sale });

export const DEFAULT_PUBLIC_CATALOG = {
    currency: 'usd',
    plans: {
        FREE: {
            key: 'FREE', name: 'Free', tagline: 'See the edge before you pay for it.', active: true, rank: 0,
            queryLimit: 10, queryPeriod: 'month', backgroundQueryLimit: 1,
            prices: { month: p(0), year: p(0) },
            features: [ 'EV and fair odds on every result', 'Every sportsbook compared side by side', '1 background search at a time'],
        },
        BASIC: {
            key: 'BASIC', name: 'Basic', tagline: 'For the bettor who checks the number first.', active: true, rank: 1,
            queryLimit: 150, queryPeriod: 'month', backgroundQueryLimit: 2,
            prices: { month: p(19, false), year: p(190, false) },
            features: [ 'Same-game parlay optimizer', 'EV, fair odds and best price across books', '2 background searches at a time'],
        },
        PRO: {
            key: 'PRO', name: 'Pro', tagline: 'Volume, speed and every market we carry.', active: true, highlight: true, rank: 2,
            queryLimit: 500, queryPeriod: 'month', backgroundQueryLimit: 3,
            prices: { month: p(49, false), year: p(490, false) },
            features: [ 'Same-game parlay optimizer', 'Player props, alt lines and derivatives', '3 background searches at a time', 'Priority support'],
        },
        ULTIMATE: {
            key: 'ULTIMATE', name: 'Ultimate', tagline: 'No ceiling. First in line for what ships next.', active: true, rank: 3,
            queryLimit: -1, queryPeriod: 'month', backgroundQueryLimit: 5,
            prices: { month: p(129, false), year: p(1290, false) },
            features: ['Same-game parlay optimizer', 'Player props, alt lines and derivatives', '5 background searches at a time', 'Early access to new features', 'Priority support'],
        },
    },
};

/** Ordered plan list from a public catalog doc (falls back to defaults per plan). */
const LIMIT_FEATURE = /^(\d[\d,]*\s+searches\s+a\s+(month|day)|unlimited\s+searches)$/i;

/** Features minus the search-limit line (the card shows the limit on its own). */
export const displayFeatures = (plan) => (plan?.features || []).filter((f) => !LIMIT_FEATURE.test(String(f).trim()));

export function planList(catalog) {
    const plans = catalog?.plans || {};
    return PLAN_ORDER.map((k) => ({ ...DEFAULT_PUBLIC_CATALOG.plans[k], ...(plans[k] || {}), key: k })).filter((pl) => pl.active !== false);
}

const PAID_STATUSES = new Set(['active', 'trialing', 'cancel_pending']);
const PAST_DUE_GRACE_MS = 7 * 24 * 3600 * 1000;

/** Same rules as the server's effectivePlan. */
export function effectivePlan(userData, { now = Date.now() } = {}) {
    const sub = userData?.subscription || {};
    const plan = String(sub.planType || 'FREE').toUpperCase();
    if (!PLAN_ORDER.includes(plan) || plan === 'FREE') return 'FREE';
    const isAdmin = String(userData?.userType || '').toLowerCase() === 'admin';
    const status = String(sub.subscriptionStatus || '').toLowerCase();
    const fromStripe = !!sub.subscriptionId || sub.source === 'stripe';
    if (sub._isPreview) return plan; // admin preview mode
    if (fromStripe && sub.livemode === false && !isAdmin && userData?.billingTester !== true) return 'FREE';
    if (PAID_STATUSES.has(status)) return plan;
    if (status === 'past_due') {
        const since = Date.parse(sub.pastDueSince || sub.lastPaymentFailedAt || '');
        return !Number.isFinite(since) || now - since <= PAST_DUE_GRACE_MS ? plan : 'FREE';
    }
    if (!fromStripe && !status) return plan;
    return 'FREE';
}

/** Is this a self-serve Stripe subscription the user can change / manage? */
export function hasStripeSubscription(userData) {
    const sub = userData?.subscription || {};
    const status = String(sub.subscriptionStatus || '').toLowerCase();
    return !!sub.subscriptionId && (PAID_STATUSES.has(status) || status === 'past_due');
}

export const formatPrice = (amount) => {
    const n = Number(amount) || 0;
    return n % 1 === 0 ? `$${n.toLocaleString('en-US')}` : `$${n.toFixed(2)}`;
};

/** Monthly-equivalent and percent saved for a yearly price. */
export function yearlySavings(plan) {
    const m = Number(plan?.prices?.month?.amount) || 0;
    const y = Number(plan?.prices?.year?.amount) || 0;
    if (!m || !y) return { perMonth: 0, pct: 0, saved: 0 };
    return { perMonth: y / 12, pct: Math.round((1 - y / (m * 12)) * 100), saved: m * 12 - y };
}

export const limitLabel = (plan) => {
    if (!plan) return '';
    if (Number(plan.queryLimit) === -1) return 'Unlimited searches';
    return `${Number(plan.queryLimit).toLocaleString('en-US')} searches a ${plan.queryPeriod === 'day' ? 'day' : 'month'}`;
};

/**
 * What the button on a plan card does for this user.
 * kind: 'current' | 'checkout' | 'change' | 'signin' | 'unavailable' | 'free' | 'manage-resume'
 */
export function planAction({ plan, interval, userData, signedIn, mode = 'live' }) {
    const current = effectivePlan(userData);
    const sub = userData?.subscription || {};
    const forSale = plan.key === 'FREE' || plan.prices?.[interval]?.[mode] === true;
    const sameInterval = (sub.interval || 'month') === interval;

    if (plan.key === 'FREE') {
        if (current === 'FREE') return { kind: 'current', label: signedIn ? 'Your plan' : 'Start free' , disabled: signedIn };
        return { kind: 'free', label: hasStripeSubscription(userData) ? 'Cancel anytime in billing' : 'Included', disabled: true };
    }
    if (!signedIn) return { kind: 'signin', label: `Get ${plan.name}`, disabled: false };
    if (current === plan.key && (!hasStripeSubscription(userData) || sameInterval)) {
        if (String(sub.subscriptionStatus) === 'cancel_pending') return { kind: 'manage-resume', label: 'Resume plan', disabled: false };
        return { kind: 'current', label: 'Current plan', disabled: true };
    }
    if (!forSale) return { kind: 'unavailable', label: 'Coming soon', disabled: true };
    if (hasStripeSubscription(userData)) {
        const up = (RANK[plan.key] || 0) > (RANK[current] || 0) || (current === plan.key && interval === 'year');
        return { kind: 'change', label: current === plan.key ? `Switch to ${interval === 'year' ? 'yearly' : 'monthly'}` : `${up ? 'Upgrade' : 'Switch'} to ${plan.name}`, disabled: false, upgrade: up };
    }
    return { kind: 'checkout', label: `Get ${plan.name}`, disabled: false };
}
